import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiSmartphone, FiShield } from 'react-icons/fi';
import './Login.css';

const Login = () => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [isNewUser, setIsNewUser] = useState(null);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  // Check if user exists when phone number is complete
  useEffect(() => {
    const checkUser = async () => {
      if (phone.length === 10) {
        setChecking(true);
        try {
          const response = await authAPI.getAuthMethods(phone);
          setIsNewUser(response.data.isNewUser);
          setHasPasskeys(response.data.hasPasskeys || false);
        } catch (error) {
          setIsNewUser(null);
          setHasPasskeys(false);
        } finally {
          setChecking(false);
        }
      } else {
        setIsNewUser(null);
        setHasPasskeys(false);
      }
    };

    const debounce = setTimeout(checkUser, 300);
    return () => clearTimeout(debounce);
  }, [phone]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    try {
      if (isNewUser) {
        const response = await authAPI.registerUser(phone);
        if (response.data.success) {
          localStorage.setItem('token', response.data.token);
          loginWithToken(response.data.token, response.data.user);
          toast.success('Welcome to DeliveryBridge!');
          navigate('/');
        }
      } else {
        const response = await authAPI.directLogin(phone);
        if (response.data.success) {
          localStorage.setItem('token', response.data.token);
          loginWithToken(response.data.token, response.data.user);
          toast.success(`Welcome back${response.data.user.name ? `, ${response.data.user.name}` : ''}!`);
          navigate('/');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!window.PublicKeyCredential) {
      toast.error('Passkey not supported on this device');
      return;
    }

    setLoading(true);
    try {
      // Get challenge from server
      const challengeResponse = await authAPI.getPasskeyChallenge(phone);
      const { challenge, allowCredentials, rpId } = challengeResponse.data;

      // Create credential request options
      const publicKeyCredentialRequestOptions = {
        challenge: Uint8Array.from(atob(challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
        allowCredentials: allowCredentials.map(cred => ({
          id: Uint8Array.from(atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
          type: cred.type,
          transports: ['internal', 'hybrid']
        })),
        timeout: 60000,
        userVerification: 'preferred',
        rpId: rpId
      };

      // Get credential
      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      // Verify with server
      const verifyResponse = await authAPI.verifyPasskey({
        phone,
        credentialId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''),
        authenticatorData: btoa(String.fromCharCode(...new Uint8Array(credential.response.authenticatorData))),
        clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
        signature: btoa(String.fromCharCode(...new Uint8Array(credential.response.signature)))
      });

      if (verifyResponse.data.success) {
        localStorage.setItem('token', verifyResponse.data.token);
        loginWithToken(verifyResponse.data.token, verifyResponse.data.user);
        toast.success('Welcome back!');
        navigate('/');
      }
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Passkey authentication cancelled');
      } else {
        toast.error(error.response?.data?.message || 'Passkey login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) return 'Please wait...';
    if (checking) return 'Checking...';
    if (isNewUser === true) return 'Register';
    if (isNewUser === false) return 'Continue';
    return 'Continue';
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <span className="login-icon">DB</span>
          <h1>DeliveryBridge</h1>
          <p>Solve the last-mile delivery gap</p>
        </div>

        <div className="login-card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <div className="phone-input">
                <span className="country-code">+91</span>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setPhone(value);
                  }}
                  maxLength={10}
                  autoComplete="tel"
                  autoFocus
                />
              </div>
              {phone.length === 10 && !checking && (
                <p className="phone-status">
                  {isNewUser ? 'New user - Create your account' : 'Welcome back!'}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || checking || phone.length !== 10}
            >
              <FiSmartphone style={{ marginRight: '0.5rem' }} />
              {getButtonText()}
            </button>

            {/* Passkey login option for returning users */}
            {hasPasskeys && !isNewUser && (
              <button
                type="button"
                className="btn btn-outline btn-block passkey-btn"
                onClick={handlePasskeyLogin}
                disabled={loading}
              >
                <FiShield style={{ marginRight: '0.5rem' }} />
                Use Passkey (Biometric)
              </button>
            )}
          </form>
        </div>

        <div className="login-features">
          <div className="feature">
            <span>Save</span>
            <span>Save detailed locations once</span>
          </div>
          <div className="feature">
            <span>Gate</span>
            <span>Auto-generate gate passes</span>
          </div>
          <div className="feature">
            <span>Share</span>
            <span>Share instructions automatically</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
